import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker } from 'react-dates';
import {
  Badge,
  Button,
  Collapse,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  addSelectedCollectionFilter,
  deleteSelectedCollectionFilter,
} from '../../actions/Collections';

import FiltersResetButton from './FiltersResetButton';

import { slugify } from '../../helpers';
import { getMinCollectionCreatedAt } from '../../helpers/Collections';
import { handleComponentParams } from '../../helpers/URLParams';

const getDateRanges = (intl) => ([
  {
    id: '1-day',
    label: intl.formatMessage({ defaultMessage: 'Last 24 hours' }),
    days: 1,
  },
  {
    id: '7-days',
    label: intl.formatMessage({ defaultMessage: 'Last 7 days' }),
    days: 7,
  },
  {
    id: '30-days',
    label: intl.formatMessage({ defaultMessage: 'Last 30 days' }),
    days: 30,
  },
  {
    id: 'custom',
    label: intl.formatMessage({ defaultMessage: 'Custom' }),
  },
]);

const getRange = (rangeId, intl) => (
  getDateRanges(intl).find(r => (r.id === rangeId))
);

const returnYears = (min) => {
  const max = moment().format('YYYY-MM-DD');
  const years = [];
  for (
    let i = parseInt(moment(min).format('YYYY'));
    i <= parseInt(moment(max).format('YYYY'));
    i += 1
  ) {
    years.push(<option key={i} value={i}>{i}</option>);
  }
  return years;
};

// eslint-disable-next-line react/prop-types
const renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => {
  const min = getMinCollectionCreatedAt();

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div>
        <Input
          type="select"
          bsSize="sm"
          value={
            moment(month).isBefore(moment(min)) ?
              moment(min).month() :
              month.month()
          }
          onChange={(e) => {
            if (month.isBefore(moment(min))) {
              onMonthSelect(moment(min), moment(min).month());
            } else {
              onMonthSelect(month, e.target.value);
            }
          }}
        >
          {moment.months().map((label, value) => (
            <option key={slugify(label)} value={value}>{label}</option>
          ))}
        </Input>
      </div>
      <div>
        <Input
          type="select"
          bsSize="sm"
          value={month.year()}
          onChange={(e) => {
            onYearSelect(month, e.target.value);
          }}
        >
          {returnYears(min)}
        </Input>
      </div>
    </div>
  );
};

class FiltersCollectionDates extends Component {
  constructor(props) {
    super(props);

    this.state = {
      collapse: false,
      focusedInput: null,
      startDate: moment().subtract(7, 'days'),
      endDate: moment(),
    };

    this.toggle = this.toggle.bind(this);
    this.handleChangeFilter = this.handleChangeFilter.bind(this);
    this.handleChangeDates = this.handleChangeDates.bind(this);
    this.handleReset = this.handleReset.bind(this);
  }

  componentDidMount() {
    handleComponentParams(
      true,
      ['date_from', 'date_to'],
      null,
      [
        (filter) => this.handleDateFilter(filter, 'from'),
        (filter) => this.handleDateFilter(filter, 'to'),
      ],
      () => {},
    );
  }

  handleDateFilter(filter, type) {
    const { startDate, endDate } = this.state;
    if (!filter) return;
    if (type === 'from') {
      const dateTimeFilter = moment(`${filter} 00:00:00`);
      if (!startDate.isSame(dateTimeFilter)) {
        this.setState({ startDate: dateTimeFilter });
      }
    } else if (type === 'to') {
      const dateTimeFilter = moment(`${filter} 23:59:59`);
      if (!endDate.isSame(dateTimeFilter)) {
        this.setState({ endDate: dateTimeFilter });
      }
    }
  }

  getDates(range) {
    if (range.id === 'custom') {
      const {
        startDate,
        endDate,
      } = this.state;
      return {
        from: startDate.format('YYYY-MM-DD 00:00:00'),
        to: endDate.format('YYYY-MM-DD 23:59:59'),
      };
    }

    return {
      from: moment().utc().subtract(range.days, 'days').format('YYYY-MM-DD HH:mm:ss'),
      to: moment().utc().subtract().format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  toggle() {
    const { collapse } = this.state;
    this.setState({ collapse: !collapse });
  }

  handleChangeFilter(e) {
    const { value } = e.target;
    const {
      intl,
      handleChangeFilters,
      addFilter,
      removeFilter,
    } = this.props;

    const range = getRange(value, intl);
    if (range) {
      const dates = this.getDates(range);
      let { label } = range;
      if (value === 'custom') {
        label = `${moment(dates.from).format('MM/DD/YYYY')} - ${moment(dates.to).format('MM/DD/YYYY')}`;
      }

      removeFilter();
      addFilter({
        type: 'dates',
        key: `${value}`,
        label,
        title: intl.formatMessage({ defaultMessage: 'Creation date' }),
      });

      if (value === 'custom') {
        handleChangeFilters({
          date: value,
          date_from: dates.from,
          date_to: dates.to,
        });
      } else {
        handleChangeFilters({
          date: value,
          date_from: null,
          date_to: null,
        });
      }
    }
  }

  handleChangeDates(dates) {
    this.setState({
      startDate: dates.startDate,
      endDate: dates.endDate,
    }, () => {
      if (dates.startDate && dates.endDate) {
        this.handleChangeFilter({ target: { value: 'custom' } });
      }
    });
  }

  handleReset() {
    const {
      handleChangeFilters,
      clearSelectedFilters,
    } = this.props;
    handleChangeFilters({
      date: null,
      date_from: null,
      date_to: null,
    });
    clearSelectedFilters();
  }

  render() {
    const {
      collapse,
      focusedInput,
      startDate,
      endDate,
    } = this.state;
    const { intl, filters } = this.props;

    return (
      <div className="files-filter upload-date-filter">
        <div className="filter-header">
          <Button onClick={this.toggle} className="filter-title">
            <i className={`fal fa-angle-${collapse ? 'up' : 'down'}`} />
            <span>{intl.formatMessage({ defaultMessage: 'Creation date' })}</span>
            {' '}
            {filters.date && (
              <Badge color="primary">
                {intl.formatMessage({ defaultMessage: '1' })}
              </Badge>
            )}
          </Button>
          {filters.date && (
            <FiltersResetButton
              handleReset={this.handleReset}
            />
          )}
        </div>
        <Collapse
          isOpen={collapse}
          className="filter-body"
        >
          <div className="filter-form upload-date-filter-form">
            {getDateRanges(intl).map(r => (
              <FormGroup key={r.id}>
                <Label for={`radioUploadDate-${r.id}`} check>
                  <Input
                    type="radio"
                    name="uploadDate"
                    id={`radioUploadDate-${r.id}`}
                    className="input-radio radio-s"
                    checked={filters.date === r.id}
                    value={r.id}
                    onChange={this.handleChangeFilter}
                  />
                  {' '}
                  <span>
                    {r.label}
                  </span>
                </Label>
              </FormGroup>
            ))}
            {filters.date === 'custom' && (
              <FormGroup>
                <DateRangePicker
                  startDate={startDate}
                  startDateId="date_from"
                  endDate={endDate}
                  endDateId="date_to"
                  numberOfMonths={1}
                  minimumNights={0}
                  withPortal={true}
                  onDatesChange={this.handleChangeDates}
                  hideKeyboardShortcutsPanel={true}
                  noBorder={true}
                  customArrowIcon={<i className="far fa-long-arrow-right" />}
                  isOutsideRange={() => false}
                  focusedInput={focusedInput}
                  onFocusChange={fi => this.setState({ focusedInput: fi })}
                  renderMonthElement={renderMonthElement}
                />
              </FormGroup>
            )}
          </div>
        </Collapse>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  filters: state.Collections.filters,
});

const mapDispatchToProps = dispatch => ({
  addFilter: (filter) => {
    dispatch(addSelectedCollectionFilter(filter));
  },
  removeFilter: () => {
    dispatch(deleteSelectedCollectionFilter('dates'));
  },
  clearSelectedFilters: () => {
    dispatch(deleteSelectedCollectionFilter('dates', null));
  },
});

FiltersCollectionDates.propTypes = {
  intl: PropTypes.object.isRequired,
  handleChangeFilters: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearSelectedFilters: PropTypes.func.isRequired,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FiltersCollectionDates));
